import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Copy, 
  ExternalLink,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Upload,
  Eye,
  Send,
  Loader2,
  Globe,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { StatusBadge } from '../components/common/StatusBadge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import ChecklistProgress from '../components/ChecklistProgress';
import { useTranslation } from '../i18n';

// SVG Icon component
const SvgIcon = ({ src, className = 'h-5 w-5' }) => (
  <img src={`/assets/${src}`} alt="" className={className} aria-hidden="true" />
);

// SVG Icon Components using assets
const ChatIcon = () => (
  <img src="/assets/chat.svg" alt="" className="h-5 w-5" aria-hidden="true" />
);

const DiscussionIcon = () => (
  <img src="/assets/discussion.svg" alt="" className="h-4 w-4" aria-hidden="true" />
);

export default function ClientDetailPage() {
  const { t } = useTranslation();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [messageInput, setMessageInput] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [suggestingReply, setSuggestingReply] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const { data: client, isLoading } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => api.get(`/clients/${clientId}`)
  });

  const { data: documents } = useQuery({
    queryKey: ['client-documents', clientId],
    queryFn: () => api.get(`/documents?client_id=${clientId}`)
  });

  const { data: office } = useQuery({
    queryKey: ['office', client?.virtual_office_id],
    queryFn: () => client?.virtual_office_id ? api.get(`/virtual-offices/${client.virtual_office_id}`) : null,
    enabled: !!client?.virtual_office_id
  });

  // Get or create conversation for this client
  const { data: conversation } = useQuery({
    queryKey: ['conversation', clientId],
    queryFn: () => api.post('/conversations/client', { client_id: clientId }),
    enabled: !!clientId
  });

  // Fetch messages with polling
  const { data: messages, refetch: refetchMessages } = useQuery({
    queryKey: ['messages', conversation?._id],
    queryFn: () => conversation?._id ? api.get(`/conversations/${conversation._id}/messages`) : [],
    enabled: !!conversation?._id,
    refetchInterval: 5000 // Poll every 5 seconds
  });

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (text) => api.post(`/conversations/${conversation._id}/messages`, { text }),
    onSuccess: () => {
      setMessageInput('');
      refetchMessages();
      toast.success('Message sent');
    },
    onError: () => {
      toast.error('Failed to send message');
    }
  });

  // Suggest reply mutation
  const suggestReplyMutation = useMutation({
    mutationFn: () => api.post(`/conversations/${conversation._id}/suggest-reply`, {}),
    onSuccess: (data) => {
      setMessageInput(data.suggested_text || '');
      setSuggestingReply(false);
    },
    onError: (error) => {
      setSuggestingReply(false);
      if (error?.status === 402 || error?.response?.status === 402) {
        navigate('/settings?tab=plan');
        toast.info('AI suggested replies require the Starter plan or higher.');
      } else {
        toast.error('Failed to generate suggestion');
      }
    }
  });

  const handleSendMessage = () => {
    if (!messageInput.trim() || !conversation?._id) return;
    sendMessageMutation.mutate(messageInput.trim());
  };

  const handleSuggestReply = () => {
    if (!conversation?._id) return;
    setSuggestingReply(true);
    suggestReplyMutation.mutate();
  };

  const toggleMessageExpand = (msgId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [msgId]: !prev[msgId]
    }));
  };

  const copyInviteLink = () => {
    const inviteUrl = `${window.location.origin}/invite/${client?.invite_token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success('Invite link copied');
  };

  // Create timeline from activity
  const timeline = [];
  
  if (client?.created_at) {
    timeline.push({
      type: 'invite_sent',
      date: client.created_at,
      title: 'Invite Sent',
      description: `Invitation created for ${client.name || 'client'}`
    });
  }
  
  if (client?.invite_status === 'opened' || client?.invite_status === 'in_progress' || client?.invite_status === 'complete') {
    timeline.push({
      type: 'invite_opened',
      date: client.updated_at,
      title: 'Invite Opened',
      description: 'Client accessed the invite link'
    });
  }
  
  documents?.forEach(doc => {
    timeline.push({
      type: 'document_uploaded',
      date: doc.created_at,
      title: 'Document Uploaded',
      description: doc.original_filename,
      document: doc
    });
  });
  
  if (client?.invite_status === 'complete') {
    timeline.push({
      type: 'complete',
      date: client.updated_at,
      title: 'Submission Complete',
      description: 'Client completed document upload'
    });
  }

  timeline.sort((a, b) => new Date(a.date) - new Date(b.date));

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="client-detail-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12" data-testid="client-not-found">
        <p className="text-muted-foreground">Client not found</p>
        <Button variant="outline" onClick={() => navigate('/clients')} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clients')} data-testid="back-button">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
              <span className="font-semibold text-accent text-lg">
                {client.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold">{client.name || 'Unnamed Client'}</h1>
              <div className="flex items-center gap-3 mt-1">
                {office && <span className="text-sm text-muted-foreground">{office.name}</span>}
                <StatusBadge status={client.invite_status} />
                <span className="language-chip">{client.preferred_language?.toUpperCase() || 'EN'}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={copyInviteLink} data-testid="copy-link-button">
            <Copy className="h-4 w-4 mr-1" />
            Copy Link
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.open(`/invite/${client.invite_token}`, '_blank')}
            data-testid="preview-button"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Preview
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content - Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="messages" data-testid="client-tabs">
            <TabsList>
              <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="messages-tab">
                <ChatIcon />
                Messages
              </TabsTrigger>
              <TabsTrigger value="timeline" className="flex items-center gap-2" data-testid="timeline-tab">
                <Clock className="h-4 w-4" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="documents" className="flex items-center gap-2" data-testid="documents-tab">
                <FileText className="h-4 w-4" />
                Documents ({documents?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* Messages Tab */}
            <TabsContent value="messages" className="mt-4" data-testid="messages-content">
              <Card className="flex flex-col h-[500px]">
                {/* Conversation Header */}
                <CardHeader className="flex-shrink-0 border-b py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                        <span className="font-medium text-accent text-sm">
                          {client.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <CardTitle className="text-base">{client.name || 'Client'}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          {office?.name || 'No office'} - {client.invite_status === 'complete' ? 'Completed' : 'Active'}
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => refetchMessages()}
                      data-testid="refresh-messages-button"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                {/* Message List */}
                <ScrollArea className="flex-1 p-4" data-testid="message-list">
                  {messages && messages.length > 0 ? (
                    <div className="space-y-4">
                      {messages.map((msg) => {
                        const isUser = msg.from?.type === 'user';
                        const isExpanded = expandedMessages[msg._id];
                        const hasTranslation = msg.translated_text && msg.translated_text !== msg.original_text;
                        
                        return (
                          <div 
                            key={msg._id} 
                            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                            data-testid={`message-${msg._id}`}
                          >
                            <div className={`max-w-[80%] ${isUser ? 'order-1' : ''}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs text-muted-foreground">
                                  {isUser ? 'You' : (client.name || 'Client')}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(msg.sent_at || msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div className={`rounded-lg p-3 ${
                                isUser 
                                  ? 'bg-primary text-primary-foreground' 
                                  : 'bg-muted'
                              }`}>
                                <p className="text-sm whitespace-pre-wrap">
                                  {isUser ? msg.original_text : (msg.translated_text || msg.original_text)}
                                </p>
                                {hasTranslation && !isUser && (
                                  <button 
                                    onClick={() => toggleMessageExpand(msg._id)}
                                    className="text-xs mt-2 flex items-center gap-1 opacity-70 hover:opacity-100"
                                  >
                                    <Globe className="h-3 w-3" />
                                    {isExpanded ? 'Hide original' : 'Show original'}
                                    <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>
                                )}
                                {isExpanded && hasTranslation && (
                                  <p className="text-xs mt-2 pt-2 border-t border-current/20 opacity-70">
                                    Original ({msg.original_language?.toUpperCase()}): {msg.original_text}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center py-8">
                      <img src="/assets/discussion.svg" alt="" className="h-16 w-16 opacity-50 mb-4" />
                      <p className="text-sm text-muted-foreground">No messages yet</p>
                      <p className="text-xs text-muted-foreground mt-1">Start a conversation with this client</p>
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t space-y-2" data-testid="message-input-area">
                  <div className="flex gap-2">
                    <Textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder="Type a message..."
                      className="min-h-[60px] resize-none"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      data-testid="message-input"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSuggestReply}
                      disabled={suggestingReply || !conversation?._id}
                      data-testid="suggest-reply-button"
                    >
                      {suggestingReply ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <SvgIcon src="bulb.svg" className="h-4 w-4 mr-1" />
                      )}
                      AI Suggest Reply
                    </Button>
                    <Button
                      onClick={handleSendMessage}
                      disabled={!messageInput.trim() || sendMessageMutation.isPending || !conversation?._id}
                      data-testid="send-message-button"
                    >
                      {sendMessageMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4 mr-1" />
                      )}
                      Send
                    </Button>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline" className="mt-4">
              <Card>
                <CardContent className="pt-6">
                  {timeline.length > 0 ? (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                      <div className="space-y-6">
                        {timeline.map((event, idx) => (
                          <div key={idx} className="relative pl-10">
                            <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                              event.type === 'complete' 
                                ? 'bg-success text-success-foreground' 
                                : event.type === 'document_uploaded'
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted text-muted-foreground'
                            }`}>
                              {event.type === 'complete' && <CheckCircle className="h-3 w-3" />}
                              {event.type === 'document_uploaded' && <Upload className="h-3 w-3" />}
                              {event.type === 'invite_sent' && <Mail className="h-3 w-3" />}
                              {event.type === 'invite_opened' && <Eye className="h-3 w-3" />}
                            </div>
                            <div className="pb-2">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-sm">{event.title}</p>
                                <time className="text-xs text-muted-foreground">
                                  {new Date(event.date).toLocaleString()}
                                </time>
                              </div>
                              <p className="text-sm text-muted-foreground">{event.description}</p>
                              {event.document && (
                                <Button 
                                  variant="link" 
                                  size="sm" 
                                  className="h-auto p-0 mt-1"
                                  onClick={() => setViewingDoc(event.document)}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  View details
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No activity yet
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-4">
              {office?.document_checklist?.length > 0 && (
                <div className="mb-4">
                  <ChecklistProgress
                    checklist={office.document_checklist}
                    documents={documents || []}
                    onViewDoc={setViewingDoc}
                  />
                </div>
              )}
              <Card>
                <CardContent className="pt-6">
                  {documents && documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map(doc => (
                        <div
                          key={doc._id}
                          className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow cursor-pointer"
                          onClick={() => setViewingDoc(doc)}
                          data-testid={`document-${doc._id}`}
                        >
                          <div className={`w-10 h-10 rounded flex items-center justify-center ${
                            doc.status === 'processed' 
                              ? 'bg-success/10' 
                              : doc.status === 'requires_review'
                                ? 'bg-warning/10'
                                : 'bg-muted'
                          }`}>
                            <FileText className={`h-5 w-5 ${
                              doc.status === 'processed' 
                                ? 'text-success' 
                                : doc.status === 'requires_review'
                                  ? 'text-warning'
                                  : 'text-muted-foreground'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {(doc.size / 1024).toFixed(1)} KB - {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <StatusBadge status={doc.status} />
                          {doc.ai_extracted_data && (
                            <div className="flex items-center gap-1 text-xs text-accent">
                              <SvgIcon src="bulb.svg" className="h-3 w-3" />
                              AI Analyzed
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No documents uploaded yet
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Rail - Client Info */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contact Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {client.contact_email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{client.contact_email}</span>
                </div>
              )}
              {client.contact_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{client.contact_phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm capitalize">
                  {client.preferred_language === 'en' ? 'English' :
                   client.preferred_language === 'es' ? 'Spanish' :
                   client.preferred_language === 'zh' ? 'Chinese' :
                   client.preferred_language === 'fr' ? 'French' :
                   client.preferred_language || 'English'}
                </span>
              </div>
            </CardContent>
          </Card>

          {office && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Virtual Office</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{office.name}</p>
                <p className="text-sm text-muted-foreground capitalize">{office.industry?.replace('_', ' ')}</p>
                {office.document_checklist?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Checklist Progress</p>
                    <div className="space-y-2">
                      {office.document_checklist.map((doc, idx) => {
                        const isUploaded = documents?.some(d => 
                          d.original_filename?.toLowerCase().includes(doc.toLowerCase().split(' ')[0])
                        );
                        return (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            {isUploaded ? (
                              <CheckCircle className="h-4 w-4 text-success" />
                            ) : (
                              <div className="w-4 h-4 rounded-full border" />
                            )}
                            <span className={isUploaded ? '' : 'text-muted-foreground'}>{doc}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={copyInviteLink}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Invite Link
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open(`/invite/${client.invite_token}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Client View
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Document Viewer Dialog */}
      <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.original_filename}</DialogTitle>
          </DialogHeader>
          {viewingDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Size</p>
                  <p className="font-medium">{(viewingDoc.size / 1024).toFixed(1)} KB</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Uploaded</p>
                  <p className="font-medium">{new Date(viewingDoc.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <StatusBadge status={viewingDoc.status} />
                </div>
                <div>
                  <p className="text-muted-foreground">Type</p>
                  <p className="font-medium">{viewingDoc.mime_type}</p>
                </div>
              </div>

              {viewingDoc.ai_extracted_data && (
                <div className="p-4 rounded-lg bg-accent/5 border border-accent/20">
                  <div className="flex items-center gap-2 mb-3">
                    <SvgIcon src="bulb.svg" className="h-4 w-4" />
                    <span className="font-medium text-sm">AI Analysis</span>
                  </div>
                  {viewingDoc.ai_extracted_data.document_type && (
                    <div className="mb-2">
                      <span className="text-xs text-muted-foreground">Detected Type: </span>
                      <span className="text-sm capitalize">{viewingDoc.ai_extracted_data.document_type}</span>
                    </div>
                  )}
                  {viewingDoc.ai_extracted_data.summary && (
                    <p className="text-sm text-muted-foreground">{viewingDoc.ai_extracted_data.summary}</p>
                  )}
                  {viewingDoc.ai_extracted_data.extracted_fields && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Extracted Fields:</p>
                      {Object.entries(viewingDoc.ai_extracted_data.extracted_fields).map(([key, value]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {viewingDoc.ai_validation && (
                <div className={`p-4 rounded-lg ${
                  viewingDoc.ai_validation.matches_expected 
                    ? 'bg-success/5 border border-success/20' 
                    : 'bg-warning/5 border border-warning/20'
                }`}>
                  <p className="text-sm font-medium mb-1">
                    {viewingDoc.ai_validation.matches_expected ? 'Validation Passed' : 'Needs Review'}
                  </p>
                  <p className="text-sm text-muted-foreground">{viewingDoc.ai_validation.message}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
